/**
 * Selected-item overlay for <ui-select> (Astryx-style).
 *
 * A reactive controller of the select, which forwards its trigger's
 * pointerdown and its content's beforetoggle. CSS Anchor Positioning keeps a
 * below-anchor starting position; this jointly solves popover top P and list
 * scrollTop S so the selected option's centre sits on the trigger value:
 * P + C - S = A. The offset is applied as margin-block-start. Horizontal
 * placement stays with CSS, which gives both labels the same start inset.
 *
 * Presentation only — selecting values must work without it.
 */

export const VIEWPORT_PADDING = 8
export const MIN_LIST_HEIGHT = 16

export default class Alignment {
  constructor(select) {
    this.select = select
    this.openMethod = null
    this.viewportChanged = this.viewportChanged.bind(this)
    select.addController(this)
  }

  hostConnected() {
    window.addEventListener("resize", this.viewportChanged)
    window.visualViewport?.addEventListener("resize", this.viewportChanged)
  }

  hostDisconnected() {
    window.removeEventListener("resize", this.viewportChanged)
    window.visualViewport?.removeEventListener("resize", this.viewportChanged)
  }

  captureOpenMethod(event) {
    this.openMethod = event.pointerType
  }

  captureKeyboardOpen() {
    this.openMethod = "keyboard"
  }

  beforeToggled(event) {
    if (event.newState !== "open") return
    if (!this.eligible) return

    this.popover.dataset.overlay = "pending"
  }

  apply() {
    if (!this.eligible) {
      this.clearPlacement()
      return false
    }

    this.popover.dataset.overlay = "pending"
    // Disable CSS flip-block and establish the below-anchor baseline before
    // measuring; offsetHeight forces that layout while still hidden.
    this.popover.offsetHeight

    let aligned = false
    try {
      aligned = tryAlignSelectedOption({
        trigger: this.trigger,
        popover: this.popover,
        list: this.popover,
        option: this.select.selectedOption
      })
    } finally {
      if (!aligned) this.clearPlacement()
    }

    return aligned
  }

  reset() {
    this.openMethod = null
    this.clearPlacement()
  }

  get eligible() {
    if (this.popover.dataset.popoverEngine !== "anchor") return false
    if (!this.select.selectedOption) return false
    if (this.hasSearch) return false
    if (isWebKitPinchZoomed()) return false
    if (this.openMethod === "mouse") return true
    if (this.openMethod === "pen") return true
    return false
  }

  // A search field would be one of the select's children, slotted into the
  // list beside the options.
  get hasSearch() {
    return Boolean(
      this.select.querySelector('[data-ui-select-search], [role="searchbox"], input:not([type="hidden"])')
    )
  }

  get trigger() {
    return this.select.shadowRoot.querySelector('[part~="trigger"]')
  }

  get popover() {
    return this.select.shadowRoot.querySelector('[part~="content"]')
  }

  clearPlacement() {
    this.popover.style.marginBlockStart = ""
    this.popover.removeAttribute("data-overlay")
  }

  // A resize can arrive before the select's first render.
  viewportChanged() {
    if (this.popover?.dataset.overlay === "aligned") this.select.hide()
  }
}

export function isWebKitPinchZoomed(
  userAgent = navigator.userAgent,
  visualViewport = window.visualViewport
) {
  const scale = visualViewport?.scale
  if (scale == null) return false
  if (Math.abs(scale - 1) <= 0.001) return false

  if (!/\bAppleWebKit\//.test(userAgent)) return false
  if (/\b(Chrome|Chromium|Edg)\//.test(userAgent)) return false
  return true
}

export function solveSelectedItemOverlay({
  targetCenter,
  labelCenter,
  listVisibleHeight,
  listContentHeight,
  viewportHeight,
  viewportPadding = VIEWPORT_PADDING
}) {
  if (![
    targetCenter,
    labelCenter,
    listVisibleHeight,
    listContentHeight,
    viewportHeight
  ].every(Number.isFinite)) {
    return null
  }

  if (listVisibleHeight < MIN_LIST_HEIGHT) return null
  if (viewportHeight <= 0) return null

  const target = targetCenter
  const popoverMin = viewportPadding
  const popoverMax = viewportHeight - viewportPadding - listVisibleHeight
  const scrollMin = 0
  const scrollMax = Math.max(0, listContentHeight - listVisibleHeight)

  if (popoverMax < popoverMin) return null

  const feasibleScrollMin = Math.max(scrollMin, popoverMin - target + labelCenter)
  const feasibleScrollMax = Math.min(scrollMax, popoverMax - target + labelCenter)
  if (feasibleScrollMax < feasibleScrollMin) return null

  const preferredScroll = labelCenter - listVisibleHeight / 2
  const listScrollTop = clamp(preferredScroll, feasibleScrollMin, feasibleScrollMax)
  const popoverTop = target - labelCenter + listScrollTop

  return { popoverTop, listScrollTop }
}

// The option is the page's slotted element, so the list never contains it;
// a rendered height shows it is in the open list.
export function collectOverlayMetrics({ trigger, popover, list, option }) {
  if (!trigger || !popover || !list || !option) return null
  if (option.offsetHeight <= 0) return null

  const value = trigger.querySelector(':scope > [data-slot="select-value"]')
  if (!value) return null

  const triggerRect = trigger.getBoundingClientRect()
  const valueRect = value.getBoundingClientRect()
  const targetCenter = valueRect.top + valueRect.height / 2
  const labelCenter = labelCenterInList(option, list)
  const listVisibleHeight = list.clientHeight
  const listContentHeight = list.scrollHeight
  const viewportHeight = window.innerHeight

  if (triggerRect.height <= 0 || valueRect.height <= 0) return null
  if (listVisibleHeight < MIN_LIST_HEIGHT) return null
  if (![ targetCenter, labelCenter, listVisibleHeight, listContentHeight, viewportHeight, triggerRect.bottom ].every(Number.isFinite)) {
    return null
  }

  return {
    targetCenter,
    triggerBottom: triggerRect.bottom,
    labelCenter,
    listVisibleHeight,
    listContentHeight,
    viewportHeight
  }
}

export function labelCenterInList(label, list) {
  const labelRect = label.getBoundingClientRect()
  const listRect = list.getBoundingClientRect()
  if (labelRect.height <= 0) return null

  return labelRect.top + labelRect.height / 2 - listRect.top + list.scrollTop
}

export function applyOverlayPlacement(popover, list, { popoverTop, listScrollTop, triggerBottom }) {
  popover.style.marginBlockStart = `${popoverTop - triggerBottom}px`
  list.scrollTop = listScrollTop
  popover.dataset.overlay = "aligned"
}

export function tryAlignSelectedOption({
  trigger,
  popover,
  list,
  option
}) {
  const metrics = collectOverlayMetrics({ trigger, popover, list, option })
  if (!metrics) return false

  const solution = solveSelectedItemOverlay(metrics)
  if (!solution) return false

  applyOverlayPlacement(popover, list, {
    popoverTop: solution.popoverTop,
    listScrollTop: solution.listScrollTop,
    triggerBottom: metrics.triggerBottom
  })
  return true
}

function clamp(value, min, max) {
  if (value < min) return min
  if (value > max) return max
  return value
}
